import moment from "moment/moment";
import { Gender, PhoneType } from "../../../types";
import CompanyMapper from "../../home/company-mapper";
import { CompanyModel } from "../../home/types";
import { AdminModel } from "../../registration/types";
import { EMPTY_PARTIAL_DATE } from "../components/validator";
import { DEFAULT_COUNTRY, DEFAULT_TELEPHONE_CODE } from "../types";
import {
  LanguageBackendType,
  LanguageFrontendType,
} from "../components/language/types";
import { ResidencePermit } from "../../cardholder-basic-information/cardholder-form/types";

import logging from "../../../util/logging";
import {
  BasicAccountApplicationDto,
  Country,
} from "../../../api/commercial/generated-types";
const log = logging("shared mappers");

export const mapPhonePageModelToRequest = (phone: PhoneType): string => {
  return phone && `${phone.code} ${phone.number && phone.number.trim()}`;
};

const mapSelectedLanguage = (
  selectedLanguage: LanguageFrontendType,
): LanguageBackendType => {
  if (LanguageFrontendType.de === selectedLanguage) {
    return LanguageBackendType.GERMAN;
  }
  if (LanguageFrontendType.en === selectedLanguage) {
    return LanguageBackendType.ENGLISH;
  }
  if (LanguageFrontendType.fr === selectedLanguage) {
    return LanguageBackendType.FRENCH;
  }
  if (LanguageFrontendType.it === selectedLanguage) {
    return LanguageBackendType.ITALIAN;
  }

  log.error(
    "Failed mapping selected language, falling back to German. Arg: " +
      selectedLanguage,
  );
  return LanguageBackendType.GERMAN;
};

export const mapDataToCreateBasicAccountAppRequest = (
  selectedLanguage: LanguageFrontendType,
  product: string,
  company: CompanyModel,
  admin: AdminModel,
  userIsRegisteringToTheSystem: boolean,
  basicAccountApplicationRef?: string,
): BasicAccountApplicationDto => {
  return {
    language: mapSelectedLanguage(selectedLanguage),
    selectedProduct: product,
    company: CompanyMapper.mapPageModelToRequest(
      company,
      basicAccountApplicationRef,
    ),
    admin: {
      gender: admin.gender as Gender,
      firstName: admin.firstName,
      lastName: admin.lastName,
      emailBusiness: admin.emailBusiness,
      mobile: mapPhonePageModelToRequest(admin.mobile),
    },
    userIsRegisteringToTheSystem,
  };
};

export const mapBooleanToYesNoString = (flag: boolean): string => {
  return flag ? "yes" : flag === false ? "no" : null;
};

export const mapYesNoStringToBoolean = (flag: string): boolean => {
  if (flag === "yes") {
    return true;
  }

  if (flag === "no") {
    return false;
  }

  return null;
};

export const mapDatePageModelToRequest = (date: string): string => {
  return date ? moment(date, "DD.MM.YYYY").format("YYYY-MM-DD") : null;
};

export const mapDateDocumentToPageModel = (date: string): string => {
  return date ? moment(date, "YYYY-MM-DD").format("DD.MM.YYYY") : null;
};

export const mapPartialDatePageModelToRequest = (date: string): string => {
  return date && date !== EMPTY_PARTIAL_DATE
    ? moment(`01.${date}`, "DD.MM.YYYY").format("YYYY-MM-DD")
    : null;
};

export const mapPartialDateDocumentToPageModel = (date: string): string => {
  return date ? moment(date, "YYYY-MM-DD").format("MM.YYYY") : null;
};

export const mapPhoneDocumentToPageModel = (phone: string): PhoneType => {
  return phone
    ? {
        code: phone.split(" ")[0] || DEFAULT_TELEPHONE_CODE,
        number: phone.substr(phone.indexOf(" ") + 1) || "",
      }
    : {
        code: DEFAULT_TELEPHONE_CODE,
        number: "",
      };
};

export const mapCountry = (country: Country): Country => {
  return country ? country : { alpha2: DEFAULT_COUNTRY };
};

export const mapNationalityString = (country: string): string => {
  return country ? country : DEFAULT_COUNTRY;
};

export const mapNationality = (nationality: Country): string => {
  return nationality ? nationality.alpha2 : DEFAULT_COUNTRY;
};

export const mapUserToCorrespondenceLanguage = (
  userLanguage: LanguageFrontendType,
): LanguageBackendType => {
  switch (userLanguage) {
    case LanguageFrontendType.de:
      return LanguageBackendType.GERMAN;
    case LanguageFrontendType.en:
      return LanguageBackendType.ENGLISH;
    case LanguageFrontendType.it:
      return LanguageBackendType.ITALIAN;
    case LanguageFrontendType.fr:
      return LanguageBackendType.FRENCH;
  }
};

export const mapIsCrossBorderWorker = (
  isCrossBorderWorker: string,
  nationality: string,
  country: Country,
): boolean => {
  return country?.alpha2 !== "CH" && nationality !== "CH"
    ? mapYesNoStringToBoolean(isCrossBorderWorker)
    : null;
};

export const mapResidencePermit = (
  residencePermit: ResidencePermit,
  isCrossBorderWorker: string,
  nationality: string,
  country: Country,
): ResidencePermit => {
  if (nationality === "CH") {
    return null;
  } else {
    if (country?.alpha2 === "CH") {
      return residencePermit;
    } else {
      if (mapYesNoStringToBoolean(isCrossBorderWorker)) {
        return ResidencePermit.OTHER;
      }
      return null;
    }
  }
};
